import root from 'window-or-global';
import talonActions from '../actions/talon.actions';
import trackingActions, {EVENT_CATEGORY, EVENT_ACTION} from '../actions/tracking.actions';
import {useEffect, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {selectTalon} from '../selectors/talon.selectors';
import {TrackingEventTypes} from 'epic-diesel-common/src/constants/Tracking';
import {TalonState} from '../reducers/talon';
import {selectEnableTalon} from '../selectors';

export type HandleCallback = (solveToken: string) => void;
export interface UseTalonProps {
    initCaptcha: (cb: HandleCallback) => void;
    talonState: TalonState;
}

const useTalon = (): UseTalonProps => {
    const dispatch = useDispatch();
    const talonState = useSelector(selectTalon);
    const enableTalon = useSelector(selectEnableTalon);
    const [callback, setCallback] = useState<HandleCallback>();

    const initCaptcha = useCallback(
        (cb: HandleCallback) => {
            if (!enableTalon) {
                return cb(null);
            }

            try {
                root.talon.execute(talonState.flowId);
                dispatch(talonActions.start());
                setCallback(() => cb);
            } catch (err) {
                console.error('Talon Captcha failed to execute.');
                dispatch(talonActions.reset());
                cb(null);
            }
        },
        [talonState.flowId]
    );

    useEffect(() => {
        if (!enableTalon || !talonState.flowId) return;
        if (root.talon) return;

        const loadTalon = () => {
            return new Promise<boolean>((resolve, reject) => {
                if (!root) return reject(new Error('Failed to load talon on dom'));
                if (root.talon) return resolve(true);
                const url =
                    root.__talon_base || 'https://talon-website-prod.ecosec.on.epicgames.com';
                const script = root.document.createElement('script');
                script.src = `${url}/talon_sdk.js`;
                script.async = true;
                script.defer = true;
                script.onload = () => {
                    resolve(true);
                };
                script.onerror = () => {
                    reject(new Error('Failed to loadTalon from cdn'));
                };
                root.document.body.appendChild(script);
            });
        };

        const initFlow = () => {
            root.talon.load({
                flow: talonState.flowId,
                onReady: () => console.log('Talon sdk is ready.'),
                onComplete: solveToken => dispatch(talonActions.updateSolveToken({solveToken})),
                onClosed: () => dispatch(talonActions.reset()),
                onError: err => dispatch(talonActions.reset())
            });
        };

        loadTalon()
            .then(initFlow)
            .catch(err => {
                dispatch(
                    trackingActions.trackCustomEvent({
                        eventCategory: EVENT_CATEGORY.TalonCaptcha,
                        eventAction: EVENT_ACTION.Error,
                        eventType: TrackingEventTypes.Interaction,
                        interactionType: 'TalonCaptchaInit',
                        data: err
                    })
                );
            });
        return () => dispatch(talonActions.reset());
    }, [talonState.flowId]);

    useEffect(() => {
        if (enableTalon && talonState.solveToken) {
            if (typeof callback === 'function') {
                callback(talonState.solveToken);
            } else {
                if (process.env.NODE_ENV === 'development') {
                    console.error('No callback given in initCaptcha');
                }
            }
        }
    }, [talonState.solveToken]);

    return {initCaptcha, talonState};
};

export default useTalon;
